// React
import React, { useState, useEffect, useContext } from 'react';

// Material UI
import { Grid, Button, Chip, Dialog, DialogContent, DialogTitle, DialogActions, Typography, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import MuiAlert from '@material-ui/lab/Alert';

// Styles
import styles from './SalesChannelForm.module.scss';

// Components
import Filters from '../../listing/Filters/Filters';
import SalesChannelTabDataGrid from './SalesChannelTabDataGrid';

// Contexts
import { ListingContext } from '../../../contexts/listing';
import { SalesChannelContext } from '../../../contexts/saleschannel';

// Hooks
import useListing from '../../../hooks/useListing';

// Label of a row in the pending changes list
const getRowLabel = (row) => row.code || row.description || row.id;

// This component
export default function SalesChannelTabListing({
	dataTypeLabel,
	dataTypeIdentificator,
	endpointListing,
	endpointModal,
	isModal = false,
	initialSort,
	initialFilters,
	getSalesChannelColumns,
	getSalesChannelRows,
	getSalesChannelFilters,
}) {
	// Listing
	const {
			activeFilters,
			saveActiveFilters,
			page,
			pageSize,
			setPage,
			setPageSize,
			sortModel,
			setSortModel,
			resetSortModel,
			refreshDataGrid,
			setRefreshDataGrid
	} = useListing(initialSort, initialFilters);

	// Sales Channel Context
	const {
		tabModalSelectedValues,
		setTabModalSelectedValues,
		tabOwnSelectedValues,
		setTabOwnSelectedValues,
		setTabIsModified,
		tabPendingAdded,
		setTabPendingAdded,
		tabPendingRemoved,
		setTabPendingRemoved,
		tabSavedCount
	} = useContext(SalesChannelContext);

	// Modal related
	const [modalOpen, setModalOpen] = useState(false);
	const handleModalOpen = () => {
			setModalOpen(true);
	};
	const handleModalClose = () => {
			setTabModalSelectedValues([]);
			setModalOpen(false);
	};

	// Reload the grid from the server once the pending changes have been saved
	useEffect(() => {
		if (tabSavedCount > 0) {
			setPage(0);
			setRefreshDataGrid(true);
		}
	}, [tabSavedCount, setPage, setRefreshDataGrid]);

	// Remove selected from tab grid (it will be visual only, the "real" deletion/addition will be handled on "Save" button click)
	const handleRemoveSelected = (event) => {
		event.preventDefault();
		if (tabOwnSelectedValues.length > 0 && window.confirm('Are you sure you want delete these items?')) {
			const selectedIds = new Set(tabOwnSelectedValues.map((row) => row.id));
			setTabPendingAdded(tabPendingAdded.filter((row) => !selectedIds.has(row.id)));
			setTabPendingRemoved([...tabPendingRemoved.filter((row) => !selectedIds.has(row.id)), ...tabOwnSelectedValues]);
			setTabOwnSelectedValues([]);
			setTabIsModified(true);
		}
	};

	// Add selected items from the modal to the tab grid (it will be visual only, the "real" deletion/addition will be handled on "Save" button click)
	const handleAddSelected = (event) => {
		event.preventDefault();
		if (tabModalSelectedValues.length > 0) {
			// Adding a row that is pending removal just cancels the removal
			const removedIds = new Set(tabPendingRemoved.map((row) => row.id));
			const selectedIds = new Set(tabModalSelectedValues.map((row) => row.id));
			const newRows = tabModalSelectedValues.filter((row) => !removedIds.has(row.id));
			setTabPendingRemoved(tabPendingRemoved.filter((row) => !selectedIds.has(row.id)));
			setTabPendingAdded([...tabPendingAdded.filter((row) => !selectedIds.has(row.id)), ...newRows]);
			setTabModalSelectedValues([]);
			setTabIsModified(true);
		}
		setModalOpen(false);
	};

	// Undo pending changes
	const handleUndoPendingAdd = (rowToUndo) => {
		setTabPendingAdded(tabPendingAdded.filter((row) => row.id !== rowToUndo.id));
	};
	const handleUndoPendingRemove = (rowToUndo) => {
		setTabPendingRemoved(tabPendingRemoved.filter((row) => row.id !== rowToUndo.id));
	};

	return (
		<ListingContext.Provider
				value={{
					typeOfData: 'sales-channel',
					endpoint: isModal ? endpointModal : endpointListing,
					pageSize,
					getColumns: getSalesChannelColumns,
					getRows: getSalesChannelRows,
					getFilters: getSalesChannelFilters,
					initialFilters,
					activeFilters,
					saveActiveFilters,
					page,
					setPage,
					setPageSize,
					sortModel,
					setSortModel,
					resetSortModel,
					refreshDataGrid,
					setRefreshDataGrid
				}}>
				<div className={styles["tab-filters"]}>
					<Filters accordionProps={{ defaultExpanded: true, className: styles['tab-filters-accordion'] }}/>
				</div>

				{!isModal &&
					<div className={styles["tab-actions"]}>
					<Grid container spacing={3}>
						<Grid item xs={12} md="auto">
							<Button
									variant="outlined"
									disableElevation={true}
									color="secondary"
									size="large"
									onClick={handleRemoveSelected}
									>
									Remove Selected
							</Button>
						</Grid>
						<Grid item xs={12} md="auto" style={{marginLeft: 'auto'}}>
							<Button
									variant="contained"
									disableElevation={true}
									onClick={handleModalOpen}
									color="secondary"
									size="large">
									Add {dataTypeLabel}
							</Button>
						</Grid>
					</Grid>
				</div>
				}

				{!isModal && (tabPendingAdded.length > 0 || tabPendingRemoved.length > 0) &&
					<MuiAlert severity="info" className={styles["tab-pending"]}>
						Unsaved changes, click "Save" to apply them.
						{tabPendingAdded.length > 0 &&
							<div className={styles["tab-pending-row"]}>
								<strong>To add:</strong>
								{tabPendingAdded.map((row) => (
									<Chip key={row.id} size="small" label={getRowLabel(row)} onDelete={() => handleUndoPendingAdd(row)}/>
								))}
							</div>
						}
						{tabPendingRemoved.length > 0 &&
							<div className={styles["tab-pending-row"]}>
								<strong>To remove:</strong>
								{tabPendingRemoved.map((row) => (
									<Chip key={row.id} size="small" label={getRowLabel(row)} onDelete={() => handleUndoPendingRemove(row)}/>
								))}
							</div>
						}
					</MuiAlert>
				}

				<div className={styles["tab-table"]}>
					<SalesChannelTabDataGrid isModal={isModal}/>
				</div>

				{!isModal &&
				<Dialog open={modalOpen} onClose={handleModalClose} fullWidth={true} maxWidth="md" className={styles['tab-modal']}>
						<DialogTitle className={styles['tab-modal-title']} disableTypography={true}>
								<Typography variant="h2" component="div">Select {dataTypeLabel}</Typography>
								<IconButton aria-label="close" className={styles['tab-modal-title-close']} onClick={handleModalClose}>
									<CloseIcon/>
								</IconButton>
						</DialogTitle>
						<DialogContent className={styles['tab-modal-content']}>
								<SalesChannelTabListing
										dataTypeLabel={dataTypeLabel}
										dataTypeIdentificator={dataTypeIdentificator}
										isModal={true}
										endpointModal={endpointModal}
										initialSort={initialSort}
										initialFilters={initialFilters}
										getSalesChannelColumns={getSalesChannelColumns}
										getSalesChannelRows={getSalesChannelRows}
										getSalesChannelFilters={getSalesChannelFilters}
								/>
						</DialogContent>
						<DialogActions className={styles['tab-modal-actions']}>
							<Button autoFocus onClick={handleAddSelected} variant="contained" color="secondary" disableElevation={true}>
								Select
							</Button>
						</DialogActions>
				</Dialog>
				}
		</ListingContext.Provider>
	);
}
